/**
 * Error Messages System
 * 
 * Provides context-specific, user-friendly error messages for different field types
 * and validation scenarios. Messages are clear, actionable, and non-technical.
 */

import { FieldType } from '../detection-patterns';

/**
 * Message types
 */
export type MessageType = 'error' | 'warning' | 'success' | 'info';

/**
 * Message context for generating contextual messages
 */
export interface MessageContext {
  /** Field type */
  fieldType: FieldType;
  /** Current field value */
  value?: string;
  /** Validation error code or message */
  errorCode?: string;
  /** Additional context data */
  context?: Record<string, any>;
}

/**
 * Field type specific error messages
 */
const FIELD_TYPE_MESSAGES: Record<FieldType, Record<string, Record<MessageType, string>>> = {
  [FieldType.PHONE]: {
    required: {
      error: 'Phone number is required',
      warning: 'Please provide your phone number',
      success: 'Phone number looks good',
      info: 'Enter your 10-digit phone number'
    },
    invalid: {
      error: 'Please enter a valid 10-digit phone number',
      warning: 'Phone number may be incomplete',
      success: 'Valid phone number format',
      info: 'Format: (XXX) XXX-XXXX'
    },
    length: {
      error: 'Phone number must be 10 digits',
      warning: 'Phone number seems too short',
      success: 'Phone number length is correct',
      info: 'Enter all 10 digits of your phone number'
    },
    format: {
      error: 'Phone number format is invalid',
      warning: 'Phone number format may need adjustment',
      success: 'Phone number format is correct',
      info: 'We\'ll format your number automatically'
    },
    area_code: {
      error: 'Area code cannot start with 0 or 1',
      warning: 'Area code may be invalid',
      success: 'Area code is valid',
      info: 'Enter a valid US area code'
    },
    exchange: {
      error: 'Phone number exchange cannot start with 0 or 1',
      warning: 'Phone number may be invalid',
      success: 'Phone number is valid',
      info: 'Standard US phone number format'
    },
    pattern: {
      error: 'This doesn\'t look like a valid phone number',
      warning: 'Phone number appears unusual',
      success: 'Phone number validated',
      info: 'Enter a standard US phone number'
    }
  },

  [FieldType.ZIP]: {
    required: {
      error: 'ZIP code is required',
      warning: 'Please provide your ZIP code',
      success: 'ZIP code entered',
      info: 'Enter your 5-digit ZIP code'
    },
    invalid: {
      error: 'Please enter a valid ZIP code',
      warning: 'ZIP code may be incomplete',
      success: 'Valid ZIP code format',
      info: 'Format: XXXXX or XXXXX-XXXX'
    },
    length: {
      error: 'ZIP code must be 5 or 9 digits',
      warning: 'ZIP code seems incomplete',
      success: 'ZIP code length is correct',
      info: 'Standard ZIP codes are 5 digits'
    },
    format: {
      error: 'ZIP code format is invalid',
      warning: 'ZIP code format needs correction',
      success: 'ZIP code format is correct',
      info: 'We\'ll format your ZIP code automatically'
    },
    pattern: {
      error: 'This doesn\'t appear to be a valid ZIP code',
      warning: 'ZIP code may not be real',
      success: 'ZIP code validated',
      info: 'Enter a real US ZIP code'
    }
  },

  [FieldType.DATE_OF_BIRTH]: {
    required: {
      error: 'Date of birth is required',
      warning: 'Please provide your birth date',
      success: 'Date of birth entered',
      info: 'Enter your date of birth'
    },
    invalid: {
      error: 'Please enter a valid date of birth',
      warning: 'Date may be incomplete',
      success: 'Valid date format',
      info: 'Format: MM/DD/YYYY'
    },
    format: {
      error: 'Date must be in MM/DD/YYYY format',
      warning: 'Date format needs correction',
      success: 'Date format is correct',
      info: 'Use MM/DD/YYYY format (e.g., 01/15/1990)'
    },
    month: {
      error: 'Month must be between 01 and 12',
      warning: 'Month value seems incorrect',
      success: 'Valid month entered',
      info: 'Enter a valid month (01-12)'
    },
    day: {
      error: 'Day must be between 01 and 31',
      warning: 'Day value seems incorrect',
      success: 'Valid day entered',
      info: 'Enter a valid day of the month'
    },
    year: {
      error: 'Please enter a valid birth year',
      warning: 'Year seems unusual',
      success: 'Valid birth year',
      info: 'Enter your 4-digit birth year'
    },
    age_min: {
      error: 'You must be at least 13 years old',
      warning: 'Age verification required',
      success: 'Age requirement met',
      info: 'Minimum age requirement: 13 years'
    },
    age_max: {
      error: 'Please check your birth year',
      warning: 'Birth year seems very early',
      success: 'Birth year accepted',
      info: 'Enter a realistic birth year'
    },
    future: {
      error: 'Birth date cannot be in the future',
      warning: 'Date seems to be in the future',
      success: 'Valid birth date',
      info: 'Enter your actual birth date'
    },
    invalid_date: {
      error: 'This date doesn\'t exist',
      warning: 'Date combination may be invalid',
      success: 'Valid date entered',
      info: 'Enter a real calendar date'
    }
  },

  [FieldType.CURRENCY]: {
    required: {
      error: 'Amount is required',
      warning: 'Please provide an amount',
      success: 'Amount entered',
      info: 'Enter a dollar amount'
    },
    invalid: {
      error: 'Please enter a valid amount',
      warning: 'Amount format needs correction',
      success: 'Valid amount format',
      info: 'Format: $1,234.56'
    },
    format: {
      error: 'Amount format is invalid',
      warning: 'Amount format needs adjustment',
      success: 'Amount format is correct',
      info: 'We\'ll format the amount automatically'
    },
    negative: {
      error: 'Amount cannot be negative',
      warning: 'Amount should be positive',
      success: 'Positive amount entered',
      info: 'Enter a positive dollar amount'
    },
    too_low: {
      error: 'Amount seems too low',
      warning: 'Amount seems unusually low - please verify',
      success: 'Amount accepted',
      info: 'Enter your actual amount'
    },
    too_high: {
      error: 'Amount seems too high',
      warning: 'Amount is very high - please verify',
      success: 'Amount accepted',
      info: 'Verify this amount is correct'
    },
    income_low: {
      error: 'Monthly income seems too low',
      warning: 'Monthly income seems low - please verify',
      success: 'Income amount accepted',
      info: 'Enter your monthly income'
    },
    income_high: {
      error: 'Monthly income is very high',
      warning: 'Monthly income is very high - please verify',
      success: 'Income amount accepted',
      info: 'Verify your monthly income'
    },
    housing_low: {
      error: 'Housing payment seems too low',
      warning: 'Housing payment seems low - please verify',
      success: 'Housing payment accepted',
      info: 'Enter your monthly housing payment'
    },
    housing_high: {
      error: 'Housing payment is very high',
      warning: 'Housing payment is very high - please verify',
      success: 'Housing payment accepted',
      info: 'Verify your housing payment amount'
    }
  },

  [FieldType.SSN]: {
    required: {
      error: 'Social Security Number is required',
      warning: 'Please provide your SSN',
      success: 'SSN entered',
      info: 'Enter your 9-digit Social Security Number'
    },
    invalid: {
      error: 'Please enter a valid 9-digit SSN',
      warning: 'SSN may be incomplete',
      success: 'Valid SSN format',
      info: 'Format: XXX-XX-XXXX'
    },
    length: {
      error: 'SSN must be 9 digits',
      warning: 'SSN seems incomplete',
      success: 'SSN length is correct',
      info: 'Enter all 9 digits of your SSN'
    },
    format: {
      error: 'SSN format is invalid',
      warning: 'SSN format needs correction',
      success: 'SSN format is correct',
      info: 'We\'ll format your SSN automatically'
    },
    area_invalid: {
      error: 'Invalid SSN area number',
      warning: 'SSN area code may be invalid',
      success: 'SSN area code is valid',
      info: 'Enter a valid SSN area number'
    },
    pattern_invalid: {
      error: 'This SSN format is not valid',
      warning: 'SSN appears to be invalid',
      success: 'SSN validated',
      info: 'Enter your actual Social Security Number'
    },
    zeros: {
      error: 'SSN cannot be all zeros or contain invalid sequences',
      warning: 'SSN format seems incorrect',
      success: 'Valid SSN format',
      info: 'Enter your real Social Security Number'
    },
    test_number: {
      error: 'Please enter your real SSN, not a test number',
      warning: 'This appears to be a test SSN',
      success: 'SSN accepted',
      info: 'Enter your actual Social Security Number'
    }
  },

  [FieldType.NAME]: {
    required: {
      error: 'Name is required',
      warning: 'Please enter your name',
      success: 'Name entered',
      info: 'Enter your full name'
    },
    invalid: {
      error: 'Spaces are not allowed in this field',
      warning: 'Name cannot contain spaces',
      success: 'Valid name format',
      info: 'Enter a single name without spaces'
    },
    format: {
      error: 'Name format is invalid',
      warning: 'Name contains invalid characters',
      success: 'Name format is correct',
      info: 'Only letters, hyphens, and apostrophes are allowed'
    }
  },

  [FieldType.UNKNOWN]: {
    required: {
      error: 'This field is required',
      warning: 'Please fill in this field',
      success: 'Field completed',
      info: 'Please enter a value'
    },
    invalid: {
      error: 'Please enter a valid value',
      warning: 'Value may need correction',
      success: 'Value accepted',
      info: 'Enter the requested information'
    },
    format: {
      error: 'Invalid format',
      warning: 'Format may need adjustment',
      success: 'Format is correct',
      info: 'Follow the expected format'
    }
  }
};

/**
 * Generic validation error mapping to specific error codes
 */
const VALIDATION_ERROR_MAPPING: Record<string, string> = {
  'This field is required': 'required',
  'Phone number must be 10 digits': 'length',
  'Please enter a valid 10-digit phone number': 'invalid',
  'Area code cannot start with 0 or 1': 'area_code',
  'Exchange code cannot start with 0 or 1': 'exchange',
  'Phone number appears to be invalid': 'pattern',
  
  'ZIP code must be 5 or 9 digits': 'length',
  'ZIP code appears to be invalid': 'pattern',
  
  'Date must be in MM/DD/YYYY format': 'format',
  'Month must be between 01 and 12': 'month',
  'Day must be between 01 and 31': 'day',
  'You must be at least 13 years old': 'age_min',
  'Please enter a valid birth year': 'year',
  'Please enter a valid date': 'invalid_date',
  
  'Please enter a valid amount': 'invalid',
  'Amount cannot be negative': 'negative',
  'Monthly income seems low - please verify': 'income_low',
  'Monthly income is very high - please verify': 'income_high',
  'Housing payment seems low - please verify': 'housing_low',
  'Housing payment is very high - please verify': 'housing_high',
  
  'SSN must be 9 digits': 'length',
  'Invalid SSN format': 'format',
  'Invalid SSN area number': 'area_invalid',
  'SSN appears to be invalid': 'pattern_invalid',

  'Spaces are not allowed in this field': 'invalid'
};

/**
 * Error messages management system
 */
export class ErrorMessages {
  /**
   * Get field type specific message
   */
  public getFieldTypeMessage(
    fieldType: FieldType,
    errorMessage: string,
    messageType: MessageType = 'error'
  ): string {
    const messages = FIELD_TYPE_MESSAGES[fieldType];
    if (!messages) {
      return this.getGenericMessage(errorMessage, messageType);
    }

    // Try to map validation error to specific error code
    const errorCode = this.mapValidationErrorToCode(errorMessage);
    const specificMessages = messages[errorCode];
    
    if (specificMessages) {
      return specificMessages[messageType];
    }

    // Fallback to invalid message
    return messages.invalid?.[messageType] || this.getGenericMessage(errorMessage, messageType);
  }

  /**
   * Get contextual message based on field and error context
   */
  public getContextualMessage(context: MessageContext, messageType: MessageType = 'error'): string {
    const { fieldType, errorCode = 'invalid', value } = context;
    
    const messages = FIELD_TYPE_MESSAGES[fieldType];
    if (!messages) {
      return this.getGenericMessage(errorCode, messageType);
    }

    const specificMessages = messages[errorCode];
    if (specificMessages) {
      return this.personalizeMessage(specificMessages[messageType], context);
    }

    // Fallback
    return messages.invalid?.[messageType] || this.getGenericMessage(errorCode, messageType);
  }

  /**
   * Get all messages for a field type and error code
   */
  public getAllMessagesForError(fieldType: FieldType, errorCode: string): Record<MessageType, string> {
    const messages = FIELD_TYPE_MESSAGES[fieldType];
    if (!messages || !messages[errorCode]) {
      return {
        error: 'Please enter a valid value',
        warning: 'Value may need correction',
        success: 'Value accepted',
        info: 'Please check your input'
      };
    }

    return messages[errorCode];
  }

  /**
   * Get help message for field type
   */
  public getHelpMessage(fieldType: FieldType): string {
    const messages = FIELD_TYPE_MESSAGES[fieldType];
    return messages?.format?.info || messages?.invalid?.info || 'Please enter the required information';
  }

  /**
   * Get validation instructions for field type
   */
  public getValidationInstructions(fieldType: FieldType): string {
    const helpMessages: Record<FieldType, string> = {
      [FieldType.PHONE]: 'Enter your 10-digit phone number. We\'ll format it automatically.',
      [FieldType.ZIP]: 'Enter your 5-digit ZIP code.',
      [FieldType.DATE_OF_BIRTH]: 'Enter your date of birth in MM/DD/YYYY format.',
      [FieldType.CURRENCY]: 'Enter a dollar amount. You can include commas and decimal points.',
      [FieldType.SSN]: 'Enter your 9-digit Social Security Number. We\'ll format it automatically.',
      [FieldType.NAME]: 'Enter your name. Do not include spaces.',
      [FieldType.UNKNOWN]: 'Please enter the requested information.'
    };

    return helpMessages[fieldType] || helpMessages[FieldType.UNKNOWN];
  }

  /**
   * Check if error message suggests user action
   */
  public isActionableError(errorMessage: string): boolean {
    const actionablePatterns = [
      /please enter/i,
      /must be/i,
      /cannot be/i,
      /should be/i,
      /verify/i,
      /check/i,
      /correct/i
    ];

    return actionablePatterns.some(pattern => pattern.test(errorMessage));
  }

  /**
   * Get error severity level
   */
  public getErrorSeverity(errorMessage: string): 'high' | 'medium' | 'low' {
    if (errorMessage.toLowerCase().includes('required')) {
      return 'high';
    }
    
    if (errorMessage.toLowerCase().includes('verify') || 
        errorMessage.toLowerCase().includes('seems')) {
      return 'low';
    }
    
    return 'medium';
  }

  /**
   * Map validation error message to error code
   */
  private mapValidationErrorToCode(errorMessage: string): string {
    return VALIDATION_ERROR_MAPPING[errorMessage] || 'invalid';
  }

  /**
   * Get generic message for unknown cases
   */
  private getGenericMessage(errorMessage: string, messageType: MessageType): string {
    const genericMessages: Record<MessageType, string> = {
      error: errorMessage || 'Please enter a valid value',
      warning: 'Please check your input',
      success: 'Input accepted',
      info: 'Please enter the requested information'
    };

    return genericMessages[messageType];
  }

  /**
   * Personalize message with context data
   */
  private personalizeMessage(message: string, context: MessageContext): string {
    let personalizedMessage = message;

    // Replace placeholders with context data
    if (context.context) {
      Object.keys(context.context).forEach(key => {
        const placeholder = `{${key}}`;
        const value = context.context![key];
        personalizedMessage = personalizedMessage.replace(placeholder, value);
      });
    }

    return personalizedMessage;
  }
}

/**
 * Default error messages instance
 */
let globalErrorMessages: ErrorMessages | null = null;

/**
 * Get global error messages instance
 */
export function getErrorMessages(): ErrorMessages {
  if (!globalErrorMessages) {
    globalErrorMessages = new ErrorMessages();
  }
  return globalErrorMessages;
}